/**
 * Desktop sidebar — also rendered inside the mobile drawer.
 *
 * Brand row · Apps section (one nav item per app, with optional count
 * badge) · System section (Settings) · spacer · Telegram footer.
 *
 * Sidebar counts come from per-app React Query queries below. They
 * read from real backend wrappers and gracefully render nothing
 * when the user is unauthenticated or the query is in flight.
 */

import { useQuery } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";

import { useApi, useAuth } from "../auth/AuthProvider";
import { habitsApi } from "../api/habits";
import { notesApi } from "../api/notes";
import { peopleApi } from "../api/people";
import { recipesApi } from "../api/recipes";
import { Icon } from "../lib/icons";
import { useT } from "../lib/i18n";
import { APPS, appPathOf } from "./apps";

export function Sidebar(): JSX.Element {
  const [, T] = useT();
  const counts = useSidebarCounts();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  return (
    <div className="vz-sidebar">
      <div className="vz-brand">
        <Icon.brand
          className="vz-brand-glyph"
          style={{ color: "var(--accent)" }}
        />
        <div className="vz-brand-name">
          vuzlyk<span className="vz-brand-cur">_</span>
        </div>
      </div>

      <div className="vz-nav-section">{T("nav.apps")}</div>
      {APPS.map((app) => {
        const Ico = app.icon;
        const count = counts[app.id];
        return (
          <NavLink
            key={app.id}
            to={appPathOf(app.id)}
            className={({ isActive }) =>
              "vz-nav-item" + (isActive ? " active" : "")
            }
            style={({ isActive }) =>
              isActive
                ? { ["--app-accent" as string]: `var(${app.tint})` }
                : undefined
            }
          >
            {({ isActive }) => (
              <>
                <Ico
                  className="vz-nav-icon"
                  style={
                    isActive
                      ? { color: `var(${app.tint})` }
                      : { color: "var(--fg-3)" }
                  }
                />
                <span>{T(app.i18nKey)}</span>
                {count !== undefined && count !== "" && (
                  <span className="vz-nav-count">{count}</span>
                )}
              </>
            )}
          </NavLink>
        );
      })}

      <div className="vz-nav-section">{T("nav.system")}</div>
      <NavLink
        to="/settings"
        className={({ isActive }) =>
          "vz-nav-item" + (isActive ? " active" : "")
        }
      >
        <Icon.settings className="vz-nav-icon" />
        <span>{T("settings")}</span>
      </NavLink>
      {isAdmin && (
        <NavLink
          to="/admin"
          className={({ isActive }) =>
            "vz-nav-item" + (isActive ? " active" : "")
          }
        >
          <Icon.settings className="vz-nav-icon" />
          <span>{T("admin.nav.label")}</span>
        </NavLink>
      )}

      <div style={{ flex: 1 }} />

      <SidebarFooter />
    </div>
  );
}

function SidebarFooter(): JSX.Element {
  const { user } = useAuth();
  // Telegram-link status hook lands in PR3+; for now show the user's email.
  return (
    <div
      style={{
        padding: "10px 8px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        color: "var(--fg-3)",
        borderTop: "1px solid var(--line-soft)",
        marginTop: 6,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: 4,
          background: "var(--c-green)",
        }}
      />
      <Icon.user style={{ width: 12, height: 12, color: "var(--fg-2)" }} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {user?.email ?? "—"}
      </span>
    </div>
  );
}

// ---- Sidebar counts (cheap React Query queries) ----

interface SidebarCounts {
  today: string | undefined;
  tasks: string | undefined;
  notes: string | undefined;
  cal: string | undefined;
  habit: string | undefined;
  shop: string | undefined;
  cook: string | undefined;
  people: string | undefined;
}

function useSidebarCounts(): SidebarCounts {
  const client = useApi();
  const { isAuthenticated } = useAuth();

  // Sidebar counts ride along with every screen change; cache for 60s
  // so flipping between apps doesn't re-issue cheap list calls. Mutations
  // (create/delete note, etc.) still invalidate the cache explicitly.
  const SIDEBAR_STALE_MS = 60_000;

  const notes = useQuery({
    enabled: isAuthenticated,
    queryKey: ["sidebar", "notes-count"],
    queryFn: () => notesApi(client).list({ limit: 50 }),
    staleTime: SIDEBAR_STALE_MS,
  });

  const habits = useQuery({
    enabled: isAuthenticated,
    queryKey: ["sidebar", "habits-count"],
    queryFn: () => habitsApi(client).list(),
    staleTime: SIDEBAR_STALE_MS,
  });

  const recipes = useQuery({
    enabled: isAuthenticated,
    queryKey: ["sidebar", "recipes-count"],
    queryFn: () => recipesApi(client).list(),
    staleTime: SIDEBAR_STALE_MS,
  });

  const peopleRequests = useQuery({
    enabled: isAuthenticated,
    queryKey: ["sidebar", "people-requests-count"],
    queryFn: () => peopleApi(client).listRequests(),
    staleTime: SIDEBAR_STALE_MS,
  });

  return {
    today: undefined,
    tasks: undefined, // wired in PR6 when TasksScreen ships
    notes:
      notes.data === undefined
        ? undefined
        : String(notes.data.notes.length + (notes.data.next_cursor !== null ? "+" : "")),
    cal: undefined, // wired in PR5 with Calendar
    habit:
      habits.data === undefined
        ? undefined
        : `${countActiveHabits(habits.data.habits)}`,
    shop: undefined, // wired in PR6
    cook:
      recipes.data === undefined ? undefined : String(recipes.data.recipes.length),
    people:
      peopleRequests.data === undefined || peopleRequests.data.incoming.length === 0
        ? undefined
        : String(peopleRequests.data.incoming.length),
  };
}

function countActiveHabits(
  habits: { archived_at: string | null }[],
): number {
  return habits.filter((h) => h.archived_at === null).length;
}
